import { useState, useEffect } from 'react';

function makeGoalUid() {
  return 'goal_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
}

export default function GoalProfileEditor({ goalTemplates, stages, onSave, saving }) {
  const [localGoals, setLocalGoals] = useState([]);
  const [editingUid, setEditingUid] = useState(null);

  useEffect(() => {
    setLocalGoals(goalTemplates || []);
  }, [goalTemplates]);

  function handleChange(uid, key, value) {
    setLocalGoals(prev => prev.map(g => g.uid === uid ? { ...g, [key]: value } : g));
  }

  function handleAdd() {
    const newGoal = {
      uid: makeGoalUid(),
      goal_name: 'New Goal Template',
      category: '',
      icon: '&#127919;',
      description: '',
      fields: [],
      stage_uid: '',
      order: localGoals.length + 1,
    };
    setLocalGoals(prev => [...prev, newGoal]);
    setEditingUid(newGoal.uid);
  }

  function handleDelete(uid) {
    if (!window.confirm('Delete this goal template?')) return;
    setLocalGoals(prev => prev.filter(g => g.uid !== uid));
    if (editingUid === uid) setEditingUid(null);
  }

  function handleSave() {
    onSave(localGoals);
  }

  function getStageName(stageUid) {
    if (!stageUid) return 'No stage';
    const stage = (stages || []).find(s => s.uid === stageUid);
    return stage ? `${stage.icon} ${stage.name}` : 'Unknown stage';
  }

  const sorted = [...localGoals].sort((a, b) => a.order - b.order);

  return (
    <div>
      <div className="pm__toolbar">
        <span className="pm__section-title">Goal Templates</span>
        <button className="btn btn--outline-primary" onClick={handleAdd}>+ Add Goal Template</button>
      </div>

      <div className="pm__goal-grid">
        {sorted.map(goal => (
          <div
            key={goal.uid}
            className="pm__goal-card"
            style={{ border: editingUid === goal.uid ? '2px solid var(--color-primary)' : undefined }}
          >
            {editingUid === goal.uid ? (
              <>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <input
                    className="pm__input"
                    style={{ width: '48px', textAlign: 'center' }}
                    value={goal.icon}
                    onChange={e => handleChange(goal.uid, 'icon', e.target.value)}
                    title="Icon (emoji)"
                  />
                  <input
                    className="pm__input"
                    style={{ flex: 1 }}
                    value={goal.goal_name}
                    onChange={e => handleChange(goal.uid, 'goal_name', e.target.value)}
                    placeholder="Goal name"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Years to Goal:</label>
                  <input
                    type="number"
                    className="pm__input"
                    style={{ width: '80px' }}
                    value={goal.category}
                    onChange={e => handleChange(goal.uid, 'category', e.target.value)}
                    placeholder="e.g. 10"
                    min="1"
                    max="50"
                  />
                </div>
                <textarea
                  className="pm__q-text-input"
                  style={{ minHeight: '36px', marginBottom: '6px' }}
                  value={goal.description || ''}
                  onChange={e => handleChange(goal.uid, 'description', e.target.value)}
                  placeholder="Description"
                />
                <select
                  className="pm__select"
                  style={{ width: '100%', marginBottom: '8px' }}
                  value={goal.stage_uid || ''}
                  onChange={e => handleChange(goal.uid, 'stage_uid', e.target.value)}
                >
                  <option value="">No stage</option>
                  {(stages || []).map(s => (
                    <option key={s.uid} value={s.uid}>{s.icon} {s.name}</option>
                  ))}
                </select>
                <div className="pm__goal-actions">
                  <button
                    className="btn btn--outline"
                    style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                    onClick={() => setEditingUid(null)}
                  >
                    Done
                  </button>
                  <button className="pm__del-btn" onClick={() => handleDelete(goal.uid)} title="Delete">&#10005;</button>
                </div>
              </>
            ) : (
              <>
                <div className="pm__goal-icon" dangerouslySetInnerHTML={{ __html: goal.icon }} />
                <div className="pm__goal-name">{goal.goal_name}</div>
                {goal.category && <span className="pm__goal-cat">{goal.category} {goal.category == 1 ? 'year' : 'years'}</span>}
                {goal.description && <div className="pm__goal-desc">{goal.description}</div>}
                <div className="pm__goal-stage" style={{ marginTop: 'auto' }}>
                  <span className="pm__pref-cat">{getStageName(goal.stage_uid)}</span>
                </div>
                <div className="pm__goal-actions">
                  <button
                    className="btn btn--outline"
                    style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                    onClick={() => setEditingUid(goal.uid)}
                  >
                    Edit
                  </button>
                  <button className="pm__del-btn" onClick={() => handleDelete(goal.uid)} title="Delete">&#10005;</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {localGoals.length === 0 && (
        <div className="pm__empty">No goal templates. Click + Add Goal Template to create one.</div>
      )}

      <div className="pm__editor-footer">
        <button
          className={`btn ${saving ? 'btn--disabled' : 'btn--primary'}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
